import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, LoadScript, Polyline,Polygon, Marker } from "@react-google-maps/api";
import hull from "convex-hull";

const API_URL = "https://iot.grotron.farm";
const containerStyle = { width: "100%", height: "500px" };
const mapCenter = { lat: 11.002555, lng: 76.997935 }; // Default center
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function VehicleTracking() {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState("");
    const [route, setRoute] = useState([]);
    const [filteredRoute, setFilteredRoute] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [vehicleId, setVehicleId] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [address, setAddress] = useState("");
    const [totalDistance, setTotalDistance] = useState(0);
    const [tripDistance, setTripDistance] = useState(0);
    const [tripArea, setTripArea] = useState(0);
    const mapRef = useRef(null);

    useEffect(() => {
        fetchVehicles();
        if (selectedVehicle) {
            fetchRoute();
        }
    }, [selectedVehicle]);

    useEffect(() => {
        if (mapRef.current && filteredRoute.length > 1) {
            adjustMapBounds(mapRef.current);
        }
    }, [filteredRoute]);

    const fetchVehicles = async () => {
        try {
            const response = await fetch(`${API_URL}/vehicles`);
            const data = await response.json();
            setVehicles(data);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        }
    };

    const fetchRoute = async () => {
        if (!selectedVehicle) return alert("Select a vehicle!");
        try {
            const response = await fetch(
                `${API_URL}/route/${selectedVehicle}?from=${fromDate}&to=${toDate}`
            );
            const data = await response.json();
            setRoute(data);
            calculateTotalDistance(data);
            extractTripOptions(data);
        } catch (error) {
            console.error("Error fetching route:", error);
        }
    };

    const extractTripOptions = (data) => {
        const trips = Array.from(
            new Map(
                data.filter(d => d.vehicle_trip_id).map(d => [d.vehicle_trip_id, d])
            ).values()
        );
        setTripOptions(trips);
    };

    const handleTripSelection = (tripId) => {
        setSelectedTrip(tripId);
        setFilteredRoute([]);
        setTimeout(() => {
            const filtered = route.filter(point => String(point.vehicle_trip_id) === String(tripId));
            setFilteredRoute(filtered);
            //calculateTripDistance(getConvexHull(filtered));
            calculateTripDistance(filtered);
        }, 10); // Small delay ensures the state updates correctly
    };

    const calculateTripDistance = (tripRoute) => {
        if (tripRoute.length < 2) {
            setTripDistance(0);
            setTripArea(0);
            return;
        }

        let distance = 0;
        for (let i = 0; i < tripRoute.length - 1; i++) {
            const { lat: lat1, lng: lng1 } = tripRoute[i];
            const { lat: lat2, lng: lng2 } = tripRoute[i + 1];
            distance += haversineDistance(lat1, lng1, lat2, lng2);
        }

        setTripDistance(distance.toFixed(3)); // Show distance in km

        // var tripPath = getConvexHull(tripRoute);
        const closedPath = [...tripRoute, tripRoute[0]]; // Closing the loop
        //const area = calculatePolygonArea(closedPath.map(point => new window.google.maps.LatLng(point.lat, point.lng)))/10000;
        const area = calculatePolygonArea(closedPath.map(point => new window.google.maps.LatLng(point.lat, point.lng))) / 10000;
        const roundedArea = area.toFixed(3);
        
        setTripArea(roundedArea);
    };


    const addVehicle = async () => {
        if (!vehicleId.trim() || !trackingNumber.trim() || !ownerName.trim() || !address.trim()) {
            return alert("Please fill all fields before adding a vehicle.");
        }

        try {
            const response = await fetch(`${API_URL}/vehicles`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    vehicle_number: vehicleId,
                    tracking_number: trackingNumber,
                    owner_name: ownerName,
                    address: address
                }),
            });

            const data = await response.json();

            if (data.code === 201) {
                alert("🚨 Vehicle number or tracking number already exists!");
            } else if (data.code === 200) {
                alert("✅ Vehicle added successfully!");
                setVehicleId("");
                setTrackingNumber("");
                setOwnerName("");
                setAddress("");
                fetchVehicles(); // Refresh the vehicle list
            } else {
                alert("❌ Unexpected error occurred. Please try again.");
            }

        } catch (error) {
            console.error("Error adding vehicle:", error);
            alert("❌ Network error. Please check your connection.");
        }
    };


    // **Calculate Total Distance (Haversine Formula)**
    const calculateTotalDistance = (routePoints) => {
        if (routePoints.length < 2) {
            setTotalDistance(0);
            return;
        }

        let distance = 0;
        for (let i = 0; i < routePoints.length - 1; i++) {
            const { lat: lat1, lng: lng1 } = routePoints[i];
            const { lat: lat2, lng: lng2 } = routePoints[i + 1];

            distance += haversineDistance(lat1, lng1, lat2, lng2);
        }

        setTotalDistance(distance.toFixed(3)); // Convert to 2 decimal places
    };

    // **Haversine Formula to Calculate Distance Between Two Points**
    const haversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of Earth in km
        const toRad = (angle) => (angle * Math.PI) / 180;

        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in km
    };

    const getConvexHull = (points) => {
        // Convert points to [x, y] format for convex-hull library
        const pointSet = points.map(({ lat, lng }) => [lng, lat]);
        
        // Compute convex hull (returns indices of points forming the hull)
        const hullIndices = hull(pointSet);
        
        // Extract only outer boundary points
        return hullIndices.map(([i]) => ({
            lat: points[i].lat,
            lng: points[i].lng,
        }));
    };

    const calculatePolygonArea = (path) => {
        if (window.google && window.google.maps && window.google.maps.geometry) {
            const area = window.google.maps.geometry.spherical.computeArea(path);
            return area.toFixed(2); // Returns area in square meters
        } else {
            console.error("Google Maps Geometry library not loaded!");
            return 0;
        }

        if (path.length < 2) return 0;

        let minLat = Math.min(...path.map(point => point.lat));
        let maxLat = Math.max(...path.map(point => point.lat));
        let minLng = Math.min(...path.map(point => point.lng));
        let maxLng = Math.max(...path.map(point => point.lng));

        const boundingBoxCoords = [
            { lat: minLat, lng: minLng }, // Bottom-left
            { lat: maxLat, lng: minLng }, // Top-left
            { lat: maxLat, lng: maxLng }, // Top-right
            { lat: minLat, lng: maxLng }, // Bottom-right
            { lat: minLat, lng: minLng }  // Closing the loop
        ];

        if (window.google && window.google.maps.geometry) {
            const area = window.google.maps.geometry.spherical.computeArea(
                boundingBoxCoords.map(coord => new window.google.maps.LatLng(coord.lat, coord.lng))
            );
            return area.toFixed(2); // Returns area in square meters
        }

        return 0;


    };

    const adjustMapBounds = (map) => {
        if (filteredRoute.length > 1) {
            const bounds = new window.google.maps.LatLngBounds();

            // Extend bounds for each point
            filteredRoute.forEach((point) => {
                bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
            });

            // Ensure fitBounds is applied after a slight delay
            setTimeout(() => {
                map.fitBounds(bounds);
                //map.panToBounds(bounds)
            }, 500);

            // Force a small zoom change before fitting bounds
            // map.setZoom(map.getZoom() - 1);
            // setTimeout(() => {
            //     map.fitBounds(bounds);

            //     const listener = window.google.maps.event.addListener(map, "idle", function () {
            //         map.panToBounds(bounds);
            //         window.google.maps.event.removeListener(listener);
            //     });
            // }, 500);
            // setTimeout(() => {
            //     map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 }); // Adds padding
            // }, 500);

            // const listener = window.google.maps.event.addListener(map, "idle", function () {
            //     map.fitBounds(bounds);
            //     map.panToBounds(bounds); // Ensure all points are visible
            //     window.google.maps.event.removeListener(listener); // Remove listener to prevent loops
            // });
        }
    };

    const getCenterPoint = (route) => {
        if (route.length === 0) return mapCenter; // Default center if no route

        const totalPoints = route.length;
        const sumLat = route.reduce((sum, point) => sum + point.lat, 0);
        const sumLng = route.reduce((sum, point) => sum + point.lng, 0);

        return {
            lat: sumLat / totalPoints,
            lng: sumLng / totalPoints,
        };
    };

    return (
        <div style={{ padding: "20px" }}>
            {/* <h2>Vehicle Tracking</h2> */}

            {/* Add Vehicle */}
            {/* <div style={{ marginBottom: "10px" }}>
                <input
                    value={vehicleId}
                    onChange={(e) => setVehicleId(e.target.value)}
                    placeholder="Vehicle Number"
                />
                <input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Tracking Number"
                />
                <input
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Owner Name"
                />
                <input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Address"
                />
                <button onClick={addVehicle}>Add Vehicle</button>
            </div> */}

            {/* Vehicle Dropdown & Date Filters */}
            <div style={{ marginBottom: "10px" }}>
                <select value={selectedVehicle} onChange={(e) => setSelectedVehicle(e.target.value)}>
                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                        <option key={v.vehicle_number} value={v.vehicle_number}>
                            {v.vehicle_number}
                        </option>
                    ))}
                </select>
                {/* <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                <button onClick={fetchRoute}>Show Route</button> */}
            </div>

            {/* **Total Distance** */}
            <h3>Total Distance: {totalDistance} km</h3>

            <div style={{ marginBottom: "10px" }}>
                <select value={selectedTrip} onChange={(e) => handleTripSelection(e.target.value)}>
                    <option value="">Select Trip</option>
                    {tripOptions.map((trip, index) => (
                        <option key={trip.vehicle_trip_id} value={trip.vehicle_trip_id}>
                            Trip {index + 1} ({new Date(trip.start_time).toLocaleString()} - {new Date(trip.stop_time).toLocaleString()})
                        </option>
                    ))}
                </select>
            </div>
            <h3>Trip Distance: {tripDistance} km</h3>
            <h3>Trip Area: {tripArea} hectares</h3>

            {/* Scrollable Route List */}
            {/* <div style={{
                height: "150px",
                overflowY: "auto",
                border: "1px solid black",
                padding: "10px",
                marginBottom: "20px"
            }}>
                <h3>Route Data:</h3>
                <h3>Trip Distance: {tripDistance} km</h3>
                {filteredRoute.length > 0 ? (
                    <>
                        {filteredRoute.map((point, index) => (
                            <li key={index}>
                                <b>#{index + 1}</b> {new Date(point.created_on).toLocaleString()} - ({point.lat}, {point.lng})
                            </li>
                        ))}
                    </>
                ) : (
                    <p>No route data available</p>
                )}
            </div> */}

            {/* Google Map with Route */}
            <LoadScript googleMapsApiKey={googleMapsApiKey} libraries={["geometry"]}>
                <GoogleMap
                    key={selectedTrip}
                    mapContainerStyle={containerStyle}
                    center={
                        filteredRoute.length > 0 ?
                            getCenterPoint(filteredRoute)
                            : mapCenter
                    }

                    zoom={17} // Default zoom
                    onLoad={(map) => {
                        mapRef.current = map;
                        adjustMapBounds(map);
                    }}
                >
                    {/* Markers */}
                    {filteredRoute.length > 0 && (
                        <>
                            <Marker
                                position={{ lat: filteredRoute[0].lat, lng: filteredRoute[0].lng }}
                                label={{
                                    text: "Start",
                                    color: "white",
                                    fontSize: "14px",
                                    fontWeight: "bold"
                                }}
                            />
                            <Marker
                                position={{
                                    lat: filteredRoute[filteredRoute.length - 1].lat,
                                    lng: filteredRoute[filteredRoute.length - 1].lng
                                }}
                                label={{
                                    text: "End",
                                    color: "white",
                                    fontSize: "14px",
                                    fontWeight: "bold"
                                }}
                            />
                        </>
                    )}

                    {/* Route Polyline */}
                    {filteredRoute.length > 1 && (
                        <Polyline
                            path={filteredRoute}
                            options={{
                                strokeColor: "#FF0000",
                                strokeOpacity: 0.8,
                                strokeWeight: 3,
                            }}
                        />
                    )}
                    {filteredRoute.length > 2 && (
                        <Polygon
                            paths={getConvexHull(filteredRoute)}
                            options={{
                                strokeColor: "#AFCBFF",
                                strokeOpacity: 0.6,
                                strokeWeight: 2,
                                fillColor: "#ADD8E6", // Light blue color
                                fillOpacity: 0.4, // Light transparent fill
                            }}
                        />
                    )}
                </GoogleMap>

            </LoadScript>
        </div>
    );
}
